import React, { useState, useEffect } from "react";
import SlidePanel from "../add_customer/SlidePanel";
import { X } from "lucide-react";

const EditAdditionalInfo = ({
  id_R,
  editAdditionalInfoOpen,
  editAdditionalDetails,
  setEditAdditionalDetails,
  handleEditAdditionalInformation,
  handleCloseEditAdditionalInfo,
  counties,
}) => {
  const [subCounties, setSubCounties] = useState([]);
  const [kraPinError, setKraPinError] = useState("");

  // ✅ KRA PIN validation
  // Format: A123456789X
  const validateKraPin = (pin) => {
    const kraRegex = /^[A-Z]\d{9}[A-Z]$/;
    return kraRegex.test(pin);
  };

  const handleKraPinChange = (e) => {
    const value = e.target.value.toUpperCase().trim();

    setEditAdditionalDetails((prev) => ({
      ...prev,
      kraPin: value,
    }));

    // Only validate when user enters a value
    if (value !== "" && !validateKraPin(value)) {
      setKraPinError("Please enter a valid KRA PIN (e.g. A123456789X)");
    } else {
      setKraPinError("");
    }
  };

  useEffect(() => {
    const selectedCounty = counties?.find(
      (c) => c?.county === editAdditionalDetails?.county,
    );

    if (selectedCounty) {
      setSubCounties(selectedCounty?.sub_counties);
    } else {
      setSubCounties([]);
    }
  }, [editAdditionalDetails?.county, counties]);

  const isFormValid =
    editAdditionalDetails &&
    Object?.entries(editAdditionalDetails)
      ?.filter(([key]) => key !== "kraPin")
      ?.every(([, val]) => val && val?.toString()?.trim() !== "") &&
    kraPinError === "";

  return (
    <SlidePanel
      isOpen={editAdditionalInfoOpen}
      onClose={handleCloseEditAdditionalInfo}
    >
      <div className="relative flex flex-col h-full bg-white rounded-[12px]">
        <div
          onClick={handleCloseEditAdditionalInfo}
          className="absolute top-0 right-0 cursor-pointer h-[40px] w-[40px] bg-[#FF0000] rounded-tr-[10px] rounded-bl-[20px] flex items-center justify-center z-10"
        >
          <X color="#ffffff" />
        </div>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="mt-5 px-5">
            <div className="flex items-center gap-[10px] mb-8">
              <p className="text-[20px] font-bold text-[#000]">
                Edit customer additional information
              </p>
            </div>

            <p className="text-[#000] text-[15px] mb-6">
              ⚠️ All the additional information below is critical system
              therefore ensure you are providing correct information. Certain
              details cannot be edited as they has been verified through legal
              identity documents.
            </p>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 pb-6">
            <div className="flex flex-col gap-5">
              <div className="flex w-full flex-col">
                <label htmlFor="firstname">KRA PIN (Optional)</label>

                <input
                  type="text"
                  value={editAdditionalDetails?.kraPin || ""}
                  onChange={handleKraPinChange}
                  maxLength={11}
                  className={`input ${
                    kraPinError
                      ? "border border-red-500 focus:border-red-500"
                      : ""
                  }`}
                />

                {kraPinError && (
                  <span className="text-red-500 text-sm mt-1">
                    {kraPinError}
                  </span>
                )}
              </div>

              <div className="flex w-full flex-col">
                <label htmlFor="firstname">Monthly Income</label>

                <input
                  type="text"
                  value={editAdditionalDetails?.income_range}
                  onChange={(e) =>
                    setEditAdditionalDetails((prev) => ({
                      ...prev,
                      income_range: e.target.value,
                    }))
                  }
                  className="input"
                />
              </div>

              <div className="flex w-full flex-col">
                <label htmlFor="employmentType">Employment Type</label>

                <select
                  id="employmentType"
                  value={editAdditionalDetails?.employment_type}
                  onChange={(e) =>
                    setEditAdditionalDetails((prev) => ({
                      ...prev,
                      employment_type: e.target.value,
                    }))
                  }
                  className="input"
                >
                  <option value="">Select Employment Type</option>
                  <option value="Employed">Employed</option>
                  <option value="Unemployed">Unemployed</option>
                  <option value="Self employed">Self employed</option>
                  <option value="Student">Student</option>
                </select>
              </div>

              <div className="flex w-full flex-col">
                <label htmlFor="firstname">Job Title</label>

                <input
                  type="text"
                  value={editAdditionalDetails?.occupation}
                  onChange={(e) =>
                    setEditAdditionalDetails((prev) => ({
                      ...prev,
                      occupation: e.target.value,
                    }))
                  }
                  className="input"
                />
              </div>

              <div className="flex w-full flex-col">
                <label htmlFor="firstname">Country of Residence</label>

                <input
                  type="text"
                  value={editAdditionalDetails?.country_of_residence}
                  onChange={(e) =>
                    setEditAdditionalDetails((prev) => ({
                      ...prev,
                      country_of_residence: e.target.value,
                    }))
                  }
                  className="input"
                />
              </div>

              <div className="flex w-full flex-col">
                <label htmlFor="county">County</label>

                <select
                  name="county"
                  id="county"
                  value={editAdditionalDetails?.county || ""}
                  onChange={(e) => {
                    const selectedCounty = e.target.value;

                    const countyData = counties?.find(
                      (c) => c?.county === selectedCounty,
                    );

                    setEditAdditionalDetails((prev) => ({
                      ...prev,
                      county: selectedCounty,
                      subcounty: "",
                    }));

                    setSubCounties(countyData ? countyData.sub_counties : []);
                  }}
                  className="input"
                >
                  <option value="">Select county</option>

                  {counties?.map((county) => (
                    <option key={county?.id} value={county?.county}>
                      {county?.county}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex w-full flex-col">
                <label htmlFor="subCounty">Sub-County</label>

                <select
                  name="subCounty"
                  id="subCounty"
                  value={editAdditionalDetails?.subcounty || ""}
                  onChange={(e) => {
                    setEditAdditionalDetails((prev) => ({
                      ...prev,
                      subcounty: e.target.value,
                    }));
                  }}
                  className="input"
                  disabled={!editAdditionalDetails?.county}
                >
                  <option value="">Select sub-county</option>

                  {subCounties?.map((subCounty) => (
                    <option key={subCounty} value={subCounty}>
                      {subCounty}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex w-full flex-col">
                <label htmlFor="firstname">Physical Address</label>

                <input
                  type="text"
                  value={editAdditionalDetails?.physical_address}
                  onChange={(e) =>
                    setEditAdditionalDetails((prev) => ({
                      ...prev,
                      physical_address: e.target.value,
                    }))
                  }
                  className="input"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 flex justify-end">
            <button
              onClick={handleEditAdditionalInformation}
              className={`w-full  px-6 py-3 rounded transition-colors duration-200 ${
                isFormValid
                  ? "bg-[#074073] text-white cursor-pointer"
                  : "bg-[#D9D9D9] text-[#FFFFFF] cursor-not-allowed"
              }`}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </SlidePanel>
  );
};

export default EditAdditionalInfo;
